/**
 * csrfInterceptor.ts — Global fetch CSRF header injection + auto-recovery
 *
 * Patches window.fetch so that every mutating request (POST/PUT/PATCH/DELETE)
 * to a /api/* path automatically includes the X-CSRF-Token header read from
 * the wr_csrf non-HttpOnly cookie.
 *
 * TWO-LAYER STRATEGY:
 *
 * Layer 1 — PROACTIVE (before first attempt):
 *   If the wr_csrf cookie is absent, the interceptor calls GET /api/auth/csrf-token
 *   BEFORE making the mutating request. The token is returned in the JSON body
 *   (not only as a Set-Cookie header), so it works even when browsers with
 *   aggressive privacy settings (Brave Shields, Firefox Enhanced Tracking
 *   Protection) block persistent cookies or discard Set-Cookie headers.
 *   This eliminates the "CSRF token missing" error on the first attempt.
 *
 * Layer 2 — RECOVERY (after 403):
 *   If a request with a token still receives HTTP 403, the interceptor assumes
 *   the token is stale (e.g. session rotated after PIN verification or
 *   deployment restart). It refreshes the token and retries exactly once.
 *
 * Why production fails but development appears unaffected:
 *   - In development, sessions are stable (dev-bypass seed user) and the
 *     wr_csrf cookie is NOT Secure-flagged, so it is always present.
 *   - In production (Brave, Firefox Strict, etc.) the Secure+SameSite:Lax
 *     persistent cookie can be blocked or delayed, making the first-time
 *     mutating request arrive without the header. The proactive fetch fixes
 *     this before the first attempt rather than reacting to a 403.
 *
 * Call installCsrfInterceptor() once at app boot (main.tsx) before any
 * API requests are made.
 */

const CSRF_COOKIE = 'wr_csrf';
const CSRF_REFRESH_ENDPOINT = '/api/auth/csrf-token';
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Pre-authentication endpoints that never need a CSRF token.
// The server-side CSRF middleware already has EXEMPT_SUFFIXES for these, but
// by also exempting them on the client side we skip the proactive CSRF-token
// round-trip entirely. This eliminates the extra GET /api/auth/csrf-token call
// that was causing latency (and potential hangs via the Bearer-token getter)
// before every POST /api/auth/verify-turnstile.
const CLIENT_PREAUTH_EXEMPT_SUFFIXES = [
  '/auth/verify-turnstile',
  '/auth/register',
  '/auth/login',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/resend-verification',
];

/** Read the CSRF token from the wr_csrf cookie, or empty string if absent. */
function getCsrfToken(): string {
  try {
    const match = document.cookie.match(/(?:^|;\s*)wr_csrf=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
}

/** Return true if the URL is an API call that should carry the CSRF header. */
function isApiUrl(input: RequestInfo | URL): boolean {
  try {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.pathname
        : (input as Request).url;
    return url.startsWith('/api') || url.includes('/api/');
  } catch {
    return false;
  }
}

/**
 * Merge extra headers into an existing HeadersInit, returning a plain object.
 * Extra entries overwrite any existing key with the same name.
 */
function mergeHeaders(
  existing: HeadersInit | undefined,
  extra: Record<string, string>,
): Record<string, string> {
  let merged: Record<string, string> = {};
  if (existing instanceof Headers) {
    existing.forEach((v, k) => { merged[k] = v; });
  } else if (Array.isArray(existing)) {
    (existing as [string, string][]).forEach(([k, v]) => { merged[k] = v; });
  } else if (existing) {
    merged = { ...(existing as Record<string, string>) };
  }
  return { ...merged, ...extra };
}

let installed = false;

export function installCsrfInterceptor(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  /**
   * Refresh the CSRF token by hitting the dedicated endpoint via originalFetch
   * (bypassing the interceptor to avoid recursion). The server:
   *   - Sets a fresh wr_csrf cookie in the response (may be discarded by
   *     privacy browsers — that's OK).
   *   - Returns the token in the JSON body so we always have it regardless
   *     of cookie policy.
   * Falls back to reading the cookie directly if the endpoint fails.
   * Returns the fresh token string, or '' on complete failure.
   */
  async function refreshCsrfToken(): Promise<string> {
    try {
      const res = await originalFetch(CSRF_REFRESH_ENDPOINT, {
        credentials: 'include',
        // Prevent the browser returning a cached 304 response — a 304 has no
        // body, res.ok is false, and we cannot extract the token from it.
        cache: 'no-store',
      });
      if (res.ok) {
        const body = (await res.json()) as { csrfToken?: string };
        if (typeof body.csrfToken === 'string' && body.csrfToken.length > 0) {
          return body.csrfToken;
        }
      }
    } catch {
      // network failure during refresh — fall through to cookie read
    }
    // Server set wr_csrf on the response; try reading it now.
    return getCsrfToken();
  }

  /** Build a patched RequestInit that includes the CSRF token header. */
  function withCsrfHeader(init: RequestInit, token: string): RequestInit {
    return {
      ...init,
      headers: mergeHeaders(init.headers, { 'X-CSRF-Token': token }),
    };
  }

  window.fetch = function (
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> {
    const method = (
      init.method ?? (input instanceof Request ? input.method : 'GET')
    ).toUpperCase();

    const isMutating = MUTATING_METHODS.has(method) && isApiUrl(input);

    // Non-mutating or non-API requests: pass through untouched.
    if (!isMutating) return originalFetch(input, init);

    // Pre-auth exempt endpoints: skip CSRF header injection entirely.
    // These routes have no session to derive a token from; the server-side
    // CSRF middleware already exempts them. Bypassing the proactive token
    // fetch here eliminates the extra GET /api/auth/csrf-token round-trip
    // (and any latency from the Bearer getter's getSession() call) that was
    // causing the verify-turnstile POST to hang for some users.
    const inputUrl =
      typeof input === 'string'
        ? input
        : input instanceof URL
        ? input.pathname
        : (input as Request).url;
    const isPreAuthExempt = CLIENT_PREAUTH_EXEMPT_SUFFIXES.some(suffix =>
      inputUrl.endsWith(suffix),
    );
    if (isPreAuthExempt) return originalFetch(input, init);

    // ── Layer 2: post-403 recovery ──────────────────────────────────────────
    // Used by both the fast path and slow path below.
    const recoverFrom403 = async (res: Response): Promise<Response> => {
      if (res.status !== 403) return res;

      // 403 — attempt a single token refresh + retry.
      const freshToken = await refreshCsrfToken();
      if (!freshToken) {
        // No session or refresh completely failed — pass the 403 to the caller.
        console.debug('[csrf] 403 received but token refresh yielded nothing — passing error to caller');
        return res;
      }

      console.debug('[csrf] 403 recovered — retrying with fresh token');
      return originalFetch(input, withCsrfHeader(init, freshToken));
    };

    const existingToken = getCsrfToken();

    if (existingToken) {
      // ── Layer 1 fast path: cookie present ──────────────────────────────────
      // Inject immediately and let Layer 2 handle any stale-token 403.
      return originalFetch(input, withCsrfHeader(init, existingToken))
        .then(recoverFrom403);
    }

    // ── Layer 1 slow path: cookie absent ───────────────────────────────────
    // PROACTIVELY fetch the token before the first attempt so the request
    // arrives at the server with a valid header. This is the key fix for
    // browsers that block or delay persistent cookies (Brave Shields,
    // Firefox Strict ETP, etc.) — the token comes from the JSON body of
    // /api/auth/csrf-token, not from the cookie, so it is always available
    // regardless of the browser's cookie policy.
    return refreshCsrfToken().then(async (freshToken) => {
      console.debug('[csrf] cookie absent — proactively fetched token before first attempt');
      const patchedInit = freshToken ? withCsrfHeader(init, freshToken) : init;
      const res = await originalFetch(input, patchedInit);
      // Layer 2: still apply recovery in case of stale token after rotation.
      return recoverFrom403(res);
    });
  };
}

export { CSRF_COOKIE };
